import { useSyncExternalStore } from 'react'

/**
 * Cookie consent, per browser. The studio sets two kinds of cookies and
 * nothing else:
 *
 *   necessary  the session that keeps you signed in, and Cloudflare's
 *              security cookies in front of it — the door cannot work
 *              without these, so they are always on.
 *   optional   Google's sign-in layer (the account chooser and the One Tap
 *              prompt). Refusing it only means Google's prompt never loads;
 *              the Continue button still signs you in when you press it.
 *
 * There is no telemetry, no advertising, no cross-site tracking of our own:
 * fonts, images and every other asset are served by the studio itself.
 */
export interface CookieChoice {
  /** Google's sign-in layer may load. False means essentials only. */
  optional: boolean
}

const STORAGE_KEY = 'ops.cookie-choice'
const VERSION = 1

interface Stored extends CookieChoice {
  v: number
}

function load(): CookieChoice | null {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null') as Stored | null
    if (!stored || stored.v !== VERSION || typeof stored.optional !== 'boolean') return null
    return { optional: stored.optional }
  } catch {
    return null
  }
}

let current: CookieChoice | null = typeof window === 'undefined' ? null : load()
const listeners = new Set<() => void>()

export function setCookieChoice(choice: CookieChoice) {
  current = choice
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...choice, v: VERSION } satisfies Stored))
  } catch {
    // Storage is blocked. The choice still applies for this visit.
  }
  listeners.forEach((listener) => listener())
}

/** Null until the visitor has chosen — the banner answers null. */
export function useCookieChoice(): CookieChoice | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current,
  )
}
